import {
  ExegesisSlateBlock,
  IAllTagsQuery,
  IBlocksByTagsResult,
  IUpdateBlocksQuery,
  OverrideSQuery,
  OverrideSResult,
  TagMentionNode,
} from '@rhizome/pgtypes';
import {
  BLOCK_REFERENCE_MENTION,
  convertDBRowToSlateNode,
  convertSlateNodeToDBRow,
} from '@rhizome/shared-slate-utils';
import { Plate, TNode } from '@udecode/plate-core';
import { MentionCombobox } from '@udecode/plate-ui-mention';
import { queryClient } from 'apps/exegesis/pages/_app';
import { useFetcher, useMutationHook } from 'apps/exegesis/utils/DataFetchers';
import { useEffect, useRef } from 'react';
import {
  RenderBlockReferences,
  useBlockReferences,
  useTagsCombobox,
} from './ComboboxHelpers';
import { ExcerptEditorToolbar } from './ExcerptEditorToolbar';
import { excerptEditorPlugins } from './usedPlugins';

export const ExcerptEditor = (
  props: OverrideSResult<IBlocksByTagsResult> & { page: number }
) => {
  const { data: allTags } = useFetcher<IAllTagsQuery>(
    [
      'AllTags',
      {
        tags: [],
        public: false,
        author: null,
      },
    ],
    {}
  );
  console.log('allTags', allTags);

  const { mutateAsync: updateBlocks } = useMutationHook<
    OverrideSQuery<IUpdateBlocksQuery>
  >({
    route: 'UpdateBlocks',
  });
  const tagComboboxItems = useTagsCombobox(
    allTags?.map((el) => {
      return el.tags!;
    }) || []
  );
  const blockReferences = useBlockReferences(props.slate_parent!);
  const editorState = useRef([
    convertDBRowToSlateNode(props),
  ] as ExegesisSlateBlock<TagMentionNode>[]);
  const updateToFlush = useRef(false);
  const updateInterval = useRef(null as any);

  // Stop the invocation of the debounced function
  // after unmounting
  useEffect(() => {
    const flushChanges = () => {
      if (!updateToFlush.current || !editorState.current) {
        return;
      }
      updateToFlush.current = false;
      // value is gauranteed to have length 1
      const { newNode, tags } = convertSlateNodeToDBRow(
        editorState.current?.[0]
      );

      updateBlocks({
        slate_content: newNode,
        slate_index: props.slate_index,
        tags: tags,
        last_edited_date: new Date(),
        block_id: props.block_id,
      });

      queryClient.removeQueries(['NoteById', { note_id: props.slate_parent }]);
      queryClient.setQueriesData<{
        pages: OverrideSResult<IBlocksByTagsResult>[][];
        pageParams: any[];
      }>(['BlocksByTags'], (oldData) => {
        if (!oldData)
          return {
            pages: [],
            pageParams: [],
          };
        console.log('OLD DATA', oldData);
        const currentPage = (oldData as any)?.pages?.[props.page]?.slice();
        for (let i = 0; i < currentPage?.length; i++) {
          if (currentPage?.[i].block_id === props.block_id) {
            const { newNode, tags: newTags } = convertSlateNodeToDBRow(
              editorState.current?.[0]
            );
            currentPage[i].slate_content = newNode;
            currentPage[i].tags = newTags;
            break;
          }
        }
        oldData.pages[props.page] = currentPage;

        return {
          pages: [...(oldData?.pages || [])],
          pageParams: oldData?.pageParams,
        };
      });
    };
    updateInterval.current = window.setInterval(flushChanges, 500);
    return () => {
      flushChanges();
      window.clearInterval(updateInterval.current);
    };
  }, [
    props.block_id,
    props.page,
    props.slate_index,
    props.slate_parent,
    updateBlocks,
  ]);

  return (
    <Plate
      id={`editor${props.block_id}`}
      plugins={excerptEditorPlugins}
      editableProps={{
        placeholder: 'Write something...',
        spellCheck: true,
        autoFocus: true,
      }}
      initialValue={[convertDBRowToSlateNode(props)]}
      onChange={(value: TNode<any>[]) => {
        editorState.current = value as ExegesisSlateBlock[];
        updateToFlush.current = true;
      }}
    >
      <ExcerptEditorToolbar />
      <MentionCombobox items={tagComboboxItems} />
      <MentionCombobox
        items={blockReferences}
        pluginKey={BLOCK_REFERENCE_MENTION}
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        //@ts-ignore this function call works lol
        onRenderItem={RenderBlockReferences}
        // component={BlockReferenceMenu}
      />
    </Plate>
  );
};
